import { Product } from '@shared/components/ui/Product';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BatchSummary } from '@shared/models/BatchSummary';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LineBatchMeta } from './LineBatchMeta';

interface SuggestedLocation {
  id: number;
  code: string;
  stock: number;
  type_id: number;
}

interface ProductToLocate {
  id: string;
  product_id?: number;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
  batch_id?: number | null;
  batch?: BatchSummary | null;
  suggested_locations?: SuggestedLocation[];
  other_locations?: SuggestedLocation[];
}

interface ProductsToLocateSectionProps {
  products: ProductToLocate[];
  onProductLocate: (productId: string, locationId: number) => void;
  searchQuery?: string;
}

interface ProductToLocateItemProps {
  product: ProductToLocate;
  isExpanded: boolean;
  onToggleExpanded: (productId: string) => void;
}

const ProductToLocateItem: React.FC<ProductToLocateItemProps> = ({ product, isExpanded, onToggleExpanded }) => {
  const productIdForDisplay = product.product_id ?? 0;
  const suggestedLocations = product.suggested_locations || [];
  const otherLocations = product.other_locations || [];
  const hasOtherLocations = otherLocations.length > 0;
  const hasLocations = suggestedLocations.length > 0 || hasOtherLocations;

  const progress =
    product.totalQuantity > 0
      ? Math.min(((product.totalQuantity - product.quantity) / product.totalQuantity) * 100, 100)
      : 0;

  const handleToggleClick = useCallback(() => {
    onToggleExpanded(product.id);
  }, [product.id, onToggleExpanded]);

  return (
    <div className="border-b pb-4 last:border-b-0 hover:bg-muted/50 bg-background border-border">
      {/* Ubicaciones - igual que Picking */}
      {hasLocations && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {suggestedLocations.map((location) => (
              <div
                key={location.id}
                className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-800"
              >
                {location.code}
                <span className="text-xs opacity-75">Stock: {location.stock}</span>
              </div>
            ))}

            {hasOtherLocations && (
              <button
                type="button"
                onClick={handleToggleClick}
                className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted transition-colors"
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="w-3 h-3" />
                <span>
                  +{otherLocations.length} ubicación{otherLocations.length !== 1 ? 'es' : ''}
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {isExpanded && hasOtherLocations && (
        <div className="mb-3 ml-4">
          <div className="text-xs text-muted-foreground mb-2">Otras ubicaciones:</div>
          <div className="flex flex-wrap gap-2">
            {otherLocations.map((location) => (
              <div
                key={location.id}
                className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm border border-gray-200 dark:border-gray-700"
              >
                {location.code}
                <span className="ml-2 text-xs opacity-75">Stock: {location.stock}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Información del producto - igual que Picking */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <Product
            id={productIdForDisplay}
            src={product.image || ''}
            name={product.name}
            code={product.code}
            acronym={product.name?.charAt(0).toUpperCase()}
            showImageModal={true}
            enableLineClamp={true}
          />
          <LineBatchMeta batch={product.batch} batchId={product.batch_id} />
        </div>

        <div className="flex flex-col items-end space-y-1 ml-2 shrink-0">
          <div className="flex items-center space-x-2">
            <span className="text-lg font-semibold text-foreground">{product.totalQuantity - product.quantity}</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-lg font-semibold text-muted-foreground">{product.totalQuantity}</span>
          </div>

          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full ${progress >= 100 ? 'bg-green-500' : progress > 0 ? 'bg-yellow-500' : 'bg-gray-300'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProductsToLocateSection: React.FC<ProductsToLocateSectionProps> = ({ products, searchQuery = '' }) => {
  const { t } = useTranslation('locationordersDetailModule');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((productId: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  if (products.length === 0) {
    const isSearching = Boolean(searchQuery && searchQuery.trim());
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          {isSearching ? t('items.search.no_results.title') : t('ui.no_products_to_locate')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {products.map((product) => (
        <ProductToLocateItem
          key={product.id}
          product={product}
          isExpanded={expandedProducts.has(product.id)}
          onToggleExpanded={toggleExpanded}
        />
      ))}
    </div>
  );
};
